import { initVersionedSchema } from '@principle-theorem/editor';
import {
  IDestinationEntityJobRunOptions,
  ITranslationMap,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ChartedCondition,
  ChartedSurface,
  ClinicalChart,
  ClinicalNote,
  ConditionConfiguration,
  ConditionConfigurationName,
  ExistingTreatmentConfigurationName,
  MockAllTeeth,
  TreatmentConfiguration,
  TreatmentPlanProposal,
  getQuadrant,
  getQuadrantIndex,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  IBrand,
  IChartedCondition,
  IConditionConfiguration,
  PerioDataPoint,
  PerioMeasurement,
  Quadrant,
  type IClinicalChart,
  type IPatient,
  type IPerioRecord,
  type IStaffer,
  type IToothRef,
  type ITreatmentConfiguration,
  type ToothNumber,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IReffable,
  Timezone,
  WithPrimaryKey,
  find$,
  getError,
  resolveSequentially,
  snapshot,
  snapshotCombineLatest,
  toISODate,
  toMoment,
  toTimestamp,
  where,
  type DocumentReference,
  type IIdentifiable,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, groupBy, uniq, uniqWith } from 'lodash';
import * as moment from 'moment-timezone';
import { combineLatest, from, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { getChartedRefs } from '../../../d4w/destination/entities/patient-treatment-plans';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientClinicalChartDestinationEntity,
  IBaseClinicalChartJobData,
  IClinicalChartMigrationData,
  IGeneratedCharts,
  PATIENT_CLINICAL_CHART_RESOURCE_TYPE,
} from '../../../destination/entities/patient-clinical-charts';
import { DEFAULT_TREATMENT_NAME } from '../../../destination/entities/patient-treatment-plans';
import { getPractitionerOrDefaultMapping } from '../../../mappings/staff';
import { ITEM_CODE_TO_CONDITION_CUSTOM_MAPPING_TYPE } from '../../../mappings/base-item-code-to-condition';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import { PatientAppointmentSourceEntity } from '../../source/entities/patient-appointments';
import {
  CORE_PRACTICE_CONDITION_MAP,
  CorePracticeMissingToothTypeItems,
  CorePracticePatientChartedItem,
  CorePracticePatientChartedItemType,
  ICorePracticePatientChartedItem,
  PatientChartedItemSourceEntity,
} from '../../source/entities/patient-charted-items';
import {
  CORE_PACTICE_PRIODONTAL_TYPE_MAP,
  CorePracticePatientPeriodontalChartItem,
  ICorePracticePatientPeriodontalChartItem,
  PatientPeriodontalChartItemSourceEntity,
  PeriodontalRowType,
} from '../../source/entities/patient-periodontal-chart-items';
import {
  CorePracticePatientPeriodontalChart,
  PATIENT_PERIODONTAL_CHART_RESOURCE_TYPE,
  PatientPeriodontalChartSourceEntity,
} from '../../source/entities/patient-periodontal-charts';
import {
  CorePracticePatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PROVIDER_RESOURCE_TYPE } from '../../source/entities/providers';
import { CorePracticeItemCodeToTreatmentMappingHandler } from '../mappings/item-code-to-treatment';
import { CorePracticeItemCodeMappingHandler } from '../mappings/item-codes';
import { CorePracticeStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patients';
import { StafferDestinationEntity } from './staff';

export const PATIENT_CLINICAL_CHART_DESTINATION_ENTITY = DestinationEntity.init(
  {
    metadata: {
      key: PATIENT_CLINICAL_CHART_RESOURCE_TYPE,
      label: 'Patient Clinical Charts',
      description: `The conversion will be done as follows:
        Each Periodontal Chart will be added as a separate clinical chart.

        A "latest" chart will be added to include all:
          - Dentition Records
          - Clinical Notes
          - Conditions
          - Flagged Treatments

        This acts as the baseline chart going forward in Principle. All of the most latest information will be on this chart. The dates for when notes, conditions, and treatments were added will be mirrored.

        Some things to be aware of:

        If no practitioner is available for the following, the preferred practitioner for the patient has been used:
          - Conditions
          - Flagged Treatments
      `,
    },
  }
);

export interface IClinicalChartJobData
  extends IBaseClinicalChartJobData<CorePracticePatient> {
  defaultTreatmentConfiguration: WithRef<ITreatmentConfiguration>;
  treatmentConfigurations: WithRef<ITreatmentConfiguration>[];
  conditionConfigurations: WithRef<IConditionConfiguration>[];
  treatmentConfigurationMappings: WithPrimaryKey<
    ITranslationMap<ITreatmentConfiguration>
  >[];
}

export class PatientClinicalChartDestinationEntity extends BasePatientClinicalChartDestinationEntity<
  CorePracticePatient,
  IClinicalChartJobData
> {
  destinationEntity = PATIENT_CLINICAL_CHART_DESTINATION_ENTITY;
  periodontalResourceType = PATIENT_PERIODONTAL_CHART_RESOURCE_TYPE;
  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    appointments: new PatientAppointmentSourceEntity(),
    periodontalChart: new PatientPeriodontalChartSourceEntity(),
    periodontalChartItems: new PatientPeriodontalChartItemSourceEntity(),
    chartedItems: new PatientChartedItemSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  customMappings = {
    staff: new CorePracticeStafferMappingHandler(),
    itemCodes: new CorePracticeItemCodeMappingHandler(),
    itemCodeToTreatment: new CorePracticeItemCodeToTreatmentMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IClinicalChartJobData[]> {
    const brand$ = PracticeMigration.brand$(migration);
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(PROVIDER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);
    const treatmentConfigurationMappings$ =
      this.customMappings.itemCodeToTreatment.getRecords(translationMap);
    const defaultTreatmentConfiguration$ = from(brand$).pipe(
      switchMap((brand) =>
        find$(
          TreatmentConfiguration.col(brand),
          where('name', '==', DEFAULT_TREATMENT_NAME)
        )
      ),
      map((config) => {
        if (!config) {
          throw new Error(
            `Default treatment configuration not found for brand: ${migration.configuration.brand.name}`
          );
        }
        return config;
      })
    );
    const treatmentConfigurations$ = brand$.pipe(
      switchMap((brand) => TreatmentConfiguration.all$(brand))
    );
    const conditionConfigurations$ = brand$.pipe(
      switchMap((brand) => ConditionConfiguration.all$(brand))
    );

    return snapshotCombineLatest([
      staff$,
      defaultTreatmentConfiguration$,
      treatmentConfigurations$,
      sourceItemCodes$,
      treatmentConfigurationMappings$,
      conditionConfigurations$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [
            staff,
            defaultTreatmentConfiguration,
            treatmentConfigurations,
            sourceItemCodes,
            treatmentConfigurationMappings,
            conditionConfigurations,
          ],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            staff,
            sourcePatient,
            defaultTreatmentConfiguration,
            treatmentConfigurations,
            sourceItemCodes,
            treatmentConfigurationMappings,
            conditionConfigurations,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IClinicalChartJobData
  ): Promise<
    | IClinicalChartMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = data.sourcePatient.data.id;
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const defaultProviderMap = getPractitionerOrDefaultMapping(
        undefined,
        data.staff
      );

      if (!defaultProviderMap?.destinationIdentifier) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `No provider for patient. Set default provider in custom mapping with id: 0`
        );
      }

      const charts = await this._buildClinicalChartData(
        migration,
        translationMap,
        defaultProviderMap.destinationIdentifier,
        sourcePatientId,
        data
      );

      return {
        sourcePatientId: sourcePatientId.toString(),
        patientRef,
        charts,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  private async _buildClinicalChartData(
    migration: WithRef<IPracticeMigration>,
    translationMap: TranslationMapHandler,
    practitionerRef: DocumentReference<IStaffer>,
    patientUid: number,
    data: IClinicalChartJobData
  ): Promise<IGeneratedCharts> {
    const practitioner = await Firestore.getDoc(practitionerRef);

    const sourcePerioCharts =
      await this.sourceEntities.periodontalChart.filterRecords(
        migration,
        'patientId',
        patientUid
      );

    const sourcePerioChartItems =
      await this.sourceEntities.periodontalChartItems.filterRecords(
        migration,
        'patientId',
        patientUid
      );

    const perioClinicalCharts = buildPerioCharts(
      sourcePerioCharts,
      sourcePerioChartItems
    );

    const chartedItems = await this.sourceEntities.chartedItems.filterRecords(
      migration,
      'patientId',
      patientUid
    );

    const clinicalCharts = await buildClinicalCharts(
      practitioner,
      migration,
      translationMap,
      chartedItems,
      data
    );

    return {
      clinicalCharts: compact(clinicalCharts),
      perioCharts: compact(perioClinicalCharts),
    };
  }
}

function buildPerioCharts(
  sourcePerioCharts: IGetRecordResponse<CorePracticePatientPeriodontalChart>[],
  sourcePerioChartItems: IGetRecordResponse<CorePracticePatientPeriodontalChartItem>[]
): (IClinicalChart & IIdentifiable)[] {
  return sourcePerioCharts.map((sourcePerioChart) => {
    const uid = sourcePerioChart.data.id.toString();
    const createdAt = toTimestamp(moment.utc(sourcePerioChart.data.chartDate));
    const perioChartItems = compact(
      sourcePerioChartItems
        .filter(
          (sourcePerioChartItem) =>
            sourcePerioChart.data.id ===
            sourcePerioChartItem.data.periodontalChartId
        )
        .map((sourcePerioChartItem) => sourcePerioChartItem.data)
    );

    const perioRecords = buildPerioRecords(perioChartItems);

    return {
      ...ClinicalChart.init({
        perioRecords,
        teeth: MockAllTeeth(),
        createdAt,
        createdBy: undefined,
        immutable: true,
      }),
      uid,
    };
  });
}

export function isConsumedByCoreDiagnosisPath(
  item: ICorePracticePatientChartedItem
): boolean {
  return (
    (item.type === CorePracticePatientChartedItemType.MissingTooth &&
      item.diagnosis === CorePracticeMissingToothTypeItems.MissingTooth) ||
    (item.type === CorePracticePatientChartedItemType.PreExistingTreatment &&
      !!item.diagnosis &&
      !!CORE_PRACTICE_CONDITION_MAP[item.diagnosis])
  );
}

export async function buildItemCodeConditions(
  chartedItems: ICorePracticePatientChartedItem[],
  translationMap: TranslationMapHandler,
  conditionConfigByRefPath: Map<string, WithRef<IConditionConfiguration>>,
  practitioner: WithRef<IStaffer>,
  timezone: Timezone
): Promise<IChartedCondition[]> {
  const itemCodeItems = chartedItems.filter(
    (item): item is ICorePracticePatientChartedItem & { itemId: number } =>
      item.itemId !== undefined &&
      item.type !== CorePracticePatientChartedItemType.Planned &&
      !isConsumedByCoreDiagnosisPath(item)
  );
  const distinctIds = uniq(itemCodeItems.map((item) => item.itemId.toString()));
  const resolved = await resolveSequentially(
    distinctIds,
    async (
      id
    ): Promise<[string, WithRef<IConditionConfiguration>] | undefined> => {
      const record = await translationMap.getBySource<IConditionConfiguration>(
        id,
        ITEM_CODE_TO_CONDITION_CUSTOM_MAPPING_TYPE
      );
      const config = record?.destinationIdentifier
        ? conditionConfigByRefPath.get(record.destinationIdentifier.path)
        : undefined;
      return config ? [id, config] : undefined;
    }
  );
  const idToConfig = new Map(compact(resolved));

  const itemsWithConfig = compact(
    itemCodeItems.map((item) => {
      const config = idToConfig.get(item.itemId.toString());
      return config ? { item, config } : undefined;
    })
  );
  const grouped = groupBy(itemsWithConfig, (entry) => entry.config.ref.path);

  return Object.values(grouped).map((entries) => {
    const config = entries[0].config;
    const items = entries.map((entry) => entry.item);
    const chartedSurfaces = uniqWith(
      items.flatMap((item) =>
        getChartedRefs(item.tooth, item.surfaces).map((chartedRef) =>
          ChartedSurface.init({
            chartedBy: stafferToNamedDoc(practitioner),
            chartedAt: toTimestamp(moment.utc(item.date).tz(timezone)),
            chartedRef,
          })
        )
      ),
      (surfaceA, surfaceB) =>
        ChartedSurface.isSameChartedRef(surfaceA, surfaceB.chartedRef)
    );
    const notes = compact(
      items.map((item) =>
        item.note
          ? ClinicalNote.init({
              owner: stafferToNamedDoc(practitioner),
              content: initVersionedSchema(item.note),
              recordDate: toISODate(
                toTimestamp(moment.utc(item.date).tz(timezone))
              ),
              createdAt: toTimestamp(moment.utc(item.date).tz(timezone)),
              updatedAt: toTimestamp(moment.utc(item.date).tz(timezone)),
            })
          : undefined
      )
    );
    return { ...ChartedCondition.fromConfig(config), chartedSurfaces, notes };
  });
}

async function buildClinicalCharts(
  practitioner: WithRef<IStaffer>,
  migration: WithRef<IPracticeMigration>,
  translationMap: TranslationMapHandler,
  chartedItems: IGetRecordResponse<CorePracticePatientChartedItem>[],
  data: IClinicalChartJobData
): Promise<(IClinicalChart & IIdentifiable)[]> {
  const missingTeeth = await CorePracticeToothBuilders.buildMissingTeeth(
    chartedItems
      .filter(
        (chartedItem) =>
          chartedItem.data.type ===
            CorePracticePatientChartedItemType.MissingTooth &&
          chartedItem.data.diagnosis ===
            CorePracticeMissingToothTypeItems.MissingTooth
      )
      .map((chartedItem) => chartedItem.data),
    practitioner,
    migration.configuration.brand,
    migration.configuration.timezone
  );

  const sourceConditions = chartedItems
    .filter(
      (chartedItem) =>
        chartedItem.data.type ===
        CorePracticePatientChartedItemType.PreExistingTreatment
    )
    .map((chartedItem) => chartedItem.data);

  const groupedConditions: Record<string, ICorePracticePatientChartedItem[]> =
    {};
  sourceConditions.forEach((condition) => {
    if (!condition.diagnosis) {
      return;
    }
    const mapping = CORE_PRACTICE_CONDITION_MAP[condition.diagnosis];
    if (!mapping) {
      return;
    }

    if (!groupedConditions[mapping]) {
      groupedConditions[mapping] = [];
    }
    groupedConditions[mapping].push(condition);
  });

  const conditions = compact(
    await resolveSequentially(
      Object.entries(groupedConditions),
      async ([conditionMap, groupedItems]) => {
        return CorePracticeToothBuilders.buildConditionFromTeeth(
          groupedItems,
          practitioner,
          migration.configuration.brand,
          migration.configuration.timezone,
          conditionMap as
            | ConditionConfigurationName
            | ExistingTreatmentConfigurationName
        );
      }
    )
  ).flat();

  const conditionConfigByRefPath = new Map(
    data.conditionConfigurations.map((config) => [config.ref.path, config])
  );
  const itemCodeConditions = await buildItemCodeConditions(
    chartedItems.map((chartedItem) => chartedItem.data),
    translationMap,
    conditionConfigByRefPath,
    practitioner,
    migration.configuration.timezone
  );

  const merged = [...missingTeeth, ...conditions];
  const byPath = new Map(
    merged.map((condition) => [condition.config.ref.path, condition])
  );
  itemCodeConditions.forEach((itemCodeCondition) => {
    const existing = byPath.get(itemCodeCondition.config.ref.path);
    if (existing) {
      const surfacesToAdd = ChartedSurface.filterSurfaceRefs(
        itemCodeCondition.chartedSurfaces,
        existing.chartedSurfaces.map((surface) => surface.chartedRef)
      );
      existing.chartedSurfaces.push(...surfacesToAdd);
      existing.notes.push(...itemCodeCondition.notes);
    } else {
      merged.push(itemCodeCondition);
      byPath.set(itemCodeCondition.config.ref.path, itemCodeCondition);
    }
  });

  return [
    {
      uid: data.sourcePatient.record.uid,
      ...ClinicalChart.init({
        conditions: merged,
        teeth: MockAllTeeth(),
        flaggedTreatment: TreatmentPlanProposal.init(),
        createdAt: toTimestamp(toMoment(migration.configuration.backupDate)),
        createdBy: stafferToNamedDoc(practitioner),
      }),
    },
  ];
}

export function buildToothRef(
  toothNumber: ToothNumber | null
): IToothRef | undefined {
  const quadrant = toothNumber ? getQuadrant(toothNumber) : undefined;
  const quadrantIndex = toothNumber ? getQuadrantIndex(toothNumber) : undefined;

  if (!quadrant || !quadrantIndex) {
    return;
  }
  return {
    quadrant,
    quadrantIndex,
  };
}

export function buildPerioRecords(
  perioChartItems: ICorePracticePatientPeriodontalChartItem[]
): IPerioRecord[] {
  const teethResults = groupBy(perioChartItems, (item) => item.tooth);

  return compact(
    Object.values(teethResults).map((toothPerioChartItems) => {
      const toothData = toothPerioChartItems[0];
      const toothRef = buildToothRef(toothData.tooth);
      if (!toothRef) {
        return;
      }

      return {
        toothRef,
        data: {
          [PerioMeasurement.Mobility]: determinePerioValue(
            toothPerioChartItems,
            PerioMeasurement.Mobility,
            PerioDataPoint.FacialDistal,
            toothRef
          ),
          [PerioMeasurement.Recession]: {
            facialMesial: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Recession,
              PerioDataPoint.FacialMesial,
              toothRef
            ),
            facialCentral: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Recession,
              PerioDataPoint.FacialCentral,
              toothRef
            ),
            facialDistal: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Recession,
              PerioDataPoint.FacialDistal,
              toothRef
            ),
            palatalMesial: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Recession,
              PerioDataPoint.PalatalMesial,
              toothRef
            ),
            palatalCentral: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Recession,
              PerioDataPoint.PalatalCentral,
              toothRef
            ),
            palatalDistal: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Recession,
              PerioDataPoint.PalatalDistal,
              toothRef
            ),
          },
          [PerioMeasurement.Pocket]: {
            facialMesial: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Pocket,
              PerioDataPoint.FacialMesial,
              toothRef
            ),
            facialCentral: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Pocket,
              PerioDataPoint.FacialCentral,
              toothRef
            ),
            facialDistal: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Pocket,
              PerioDataPoint.FacialDistal,
              toothRef
            ),
            palatalMesial: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Pocket,
              PerioDataPoint.PalatalMesial,
              toothRef
            ),
            palatalCentral: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Pocket,
              PerioDataPoint.PalatalCentral,
              toothRef
            ),
            palatalDistal: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Pocket,
              PerioDataPoint.PalatalDistal,
              toothRef
            ),
          },
          [PerioMeasurement.Bleeding]: {
            facialMesial: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Bleeding,
              PerioDataPoint.FacialMesial,
              toothRef
            ),
            facialCentral: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Bleeding,
              PerioDataPoint.FacialCentral,
              toothRef
            ),
            facialDistal: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Bleeding,
              PerioDataPoint.FacialDistal,
              toothRef
            ),
            palatalMesial: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Bleeding,
              PerioDataPoint.PalatalMesial,
              toothRef
            ),
            palatalCentral: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Bleeding,
              PerioDataPoint.PalatalCentral,
              toothRef
            ),
            palatalDistal: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Bleeding,
              PerioDataPoint.PalatalDistal,
              toothRef
            ),
          },
          [PerioMeasurement.Furcation]: {
            facialMesial: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Furcation,
              PerioDataPoint.FacialMesial,
              toothRef
            ),
            facialCentral: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Furcation,
              PerioDataPoint.FacialCentral,
              toothRef
            ),
            facialDistal: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Furcation,
              PerioDataPoint.FacialDistal,
              toothRef
            ),
            palatalMesial: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Furcation,
              PerioDataPoint.PalatalMesial,
              toothRef
            ),
            palatalCentral: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Furcation,
              PerioDataPoint.PalatalCentral,
              toothRef
            ),
            palatalDistal: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Furcation,
              PerioDataPoint.PalatalDistal,
              toothRef
            ),
          },
          [PerioMeasurement.Suppuration]: {
            facialMesial: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Suppuration,
              PerioDataPoint.FacialMesial,
              toothRef
            ),
            facialCentral: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Suppuration,
              PerioDataPoint.FacialCentral,
              toothRef
            ),
            facialDistal: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Suppuration,
              PerioDataPoint.FacialDistal,
              toothRef
            ),
            palatalMesial: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Suppuration,
              PerioDataPoint.PalatalMesial,
              toothRef
            ),
            palatalCentral: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Suppuration,
              PerioDataPoint.PalatalCentral,
              toothRef
            ),
            palatalDistal: determinePerioValue(
              toothPerioChartItems,
              PerioMeasurement.Suppuration,
              PerioDataPoint.PalatalDistal,
              toothRef
            ),
          },
        },
      };
    })
  );
}

export function determinePerioValue(
  records: ICorePracticePatientPeriodontalChartItem[],
  measurement: PerioMeasurement,
  measurementPoint: PerioDataPoint,
  toothRef: IToothRef
): number {
  const rightSectionDataPointMap: Record<PerioDataPoint, number> = {
    [PerioDataPoint.FacialDistal]: 1,
    [PerioDataPoint.FacialCentral]: 2,
    [PerioDataPoint.FacialMesial]: 3,
    [PerioDataPoint.PalatalDistal]: 4,
    [PerioDataPoint.PalatalCentral]: 5,
    [PerioDataPoint.PalatalMesial]: 6,
  };

  const leftSectionDataPointMap: Record<PerioDataPoint, number> = {
    [PerioDataPoint.FacialMesial]: 1,
    [PerioDataPoint.FacialCentral]: 2,
    [PerioDataPoint.FacialDistal]: 3,
    [PerioDataPoint.PalatalMesial]: 4,
    [PerioDataPoint.PalatalCentral]: 5,
    [PerioDataPoint.PalatalDistal]: 6,
  };

  const matchingRecord = records.find((record) => {
    const perioMeasurementType = CORE_PACTICE_PRIODONTAL_TYPE_MAP[record.type];

    if (!perioMeasurementType || perioMeasurementType !== measurement) {
      return false;
    }

    const dataPointMapNumber =
      record.periodontalRow === PeriodontalRowType.Buccal
        ? record.periodontalColumn
        : record.periodontalColumn + 3;

    if (
      measurement === PerioMeasurement.Mobility &&
      perioMeasurementType === PerioMeasurement.Mobility
    ) {
      if (record.periodontalRow === PeriodontalRowType.Palatal) {
        return false;
      }
      return record.periodontalColumn === 1;
    }

    const dataPointMap = [
      Quadrant.AdultUpperRight,
      Quadrant.AdultLowerRight,
      Quadrant.DeciduousUpperRight,
      Quadrant.DeciduousLowerRight,
    ].includes(toothRef.quadrant)
      ? rightSectionDataPointMap
      : leftSectionDataPointMap;

    const isMatchingDataPoint =
      dataPointMap[measurementPoint] === dataPointMapNumber;

    if (!isMatchingDataPoint) {
      return false;
    }

    return true;
  });

  if (!matchingRecord) {
    return 0;
  }

  return matchingRecord.value;
}

export class CorePracticeToothBuilders {
  static async buildMissingTeeth(
    chartedItems: ICorePracticePatientChartedItem[],
    practitioner: WithRef<IStaffer>,
    brand: IReffable<IBrand>,
    timezone: Timezone
  ): Promise<IChartedCondition[]> {
    return CorePracticeToothBuilders.buildConditionFromTeeth(
      chartedItems,
      practitioner,
      brand,
      timezone,
      ConditionConfigurationName.MissingTooth
    );
  }

  static async buildConditionFromTeeth(
    chartedItems: ICorePracticePatientChartedItem[],
    practitioner: WithRef<IStaffer>,
    brand: IReffable<IBrand>,
    timezone: Timezone,
    configurationName:
      | ConditionConfigurationName
      | ExistingTreatmentConfigurationName
  ): Promise<IChartedCondition[]> {
    const foundCondition = await snapshot(
      find$(
        ConditionConfiguration.col(brand),
        where('name', '==', configurationName)
      )
    );

    if (!foundCondition) {
      throw new Error(`${configurationName} condition is missing`);
    }

    const notes = chartedItems.map((chartedItem) => {
      if (!chartedItem.note) {
        return;
      }
      const createdAt = toTimestamp(moment.utc(chartedItem.date).tz(timezone));
      return ClinicalNote.init({
        owner: stafferToNamedDoc(practitioner),
        content: initVersionedSchema(chartedItem.note),
        recordDate: toISODate(createdAt),
        createdAt: createdAt,
        updatedAt: createdAt,
      });
    });

    const chartedSurfaces = chartedItems.flatMap((chartedItem) => {
      return getChartedRefs(chartedItem.tooth, chartedItem.surfaces).map(
        (chartedRef) =>
          ChartedSurface.init({
            chartedBy: stafferToNamedDoc(practitioner),
            chartedAt: toTimestamp(),
            chartedRef,
          })
      );
    });

    return [
      {
        ...ChartedCondition.fromConfig(foundCondition),
        chartedSurfaces,
        notes: compact(notes),
      },
    ];
  }
}
